import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { Store } from "lucide-react";
import { api, getToken, setToken } from "@/lib/api";
import Kiosk from "@/pages/Kiosk";
import Admin from "@/pages/Admin";
import Auth from "@/pages/Auth";
import { KioskPinDialog } from "@/components/app/KioskPinDialog";

const useSecretTap = (action, count = 10) => {
  const taps = useRef([]);
  return () => {
    const now = Date.now();
    taps.current = [...taps.current.filter((t) => now - t < 5000), now];
    if (taps.current.length >= count) {
      taps.current = [];
      action();
    }
  };
};

function App() {
  const [user, setUser] = useState(null);
  const [view, setView] = useState("kiosk");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const refresh = () => api.post("/auth/refresh").then((r) => setToken(r.data.token)).catch(() => {});
    if (getToken()) {
      api
        .get("/auth/me")
        .then((r) => {
          setUser(r.data);
          setView(r.data.role === "superadmin" ? "admin" : "kiosk");
          refresh();
        })
        .catch(() => setToken(null))
        .finally(() => setReady(true));
    } else {
      setReady(true);
    }
    // Kiosk sürekli açık kaldığı için oturumu 6 saatte bir yenile
    const t = setInterval(() => getToken() && refresh(), 6 * 3600 * 1000);
    return () => clearInterval(t);
  }, []);

  const onAuthed = (u) => {
    setUser(u);
    setView(u.role === "superadmin" ? "admin" : "kiosk");
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setView("kiosk");
  };

  const [pinOpen, setPinOpen] = useState(false);
  const secretToAdmin = useSecretTap(() => setPinOpen(true));
  const secretToLogin = useSecretTap(() => { window.location.href = "/"; });

  if (!ready) return null;

  const publicCode = (new URLSearchParams(window.location.search).get("sube") || "").trim().toUpperCase();
  if (publicCode) {
    return (
      <div className="App">
        <Toaster position="top-center" richColors />
        <div className="sticky top-0 z-50 bg-white border-b border-[var(--fb-border)]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
            <img
              src="/logo.jpeg"
              alt="Deny's Burger & Fries"
              data-testid="public-logo-secret"
              onClick={secretToLogin}
              style={{ touchAction: "manipulation" }}
              className="h-16 w-auto object-contain select-none"
            />
          </div>
        </div>
        <Kiosk publicCode={publicCode} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="App">
        <Toaster position="top-center" richColors />
        <Auth onAuthed={onAuthed} />
      </div>
    );
  }

  const isBranch = user.role !== "superadmin";

  return (
    <div className="App">
      <Toaster position="top-center" richColors />

      <div className="sticky top-0 z-50 bg-white border-b border-[var(--fb-border)]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <img
            src="/logo.jpeg"
            alt="Deny's Burger & Fries"
            data-testid="kiosk-logo-secret"
            onClick={isBranch && view === "kiosk" ? secretToAdmin : undefined}
            style={{ touchAction: "manipulation" }}
            className="h-16 w-auto object-contain select-none"
            draggable={false}
          />

          {isBranch &&
            (view === "kiosk" ? null : (
              <button
                data-testid="open-kiosk-btn"
                onClick={() => setView("kiosk")}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold text-white bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] transition-colors"
              >
                <Store className="w-4 h-4" />
                <span>Kiosk'u Aç</span>
              </button>
            ))}
        </div>
      </div>

      {isBranch && view === "kiosk" ? (
        <Kiosk user={user} />
      ) : (
        <Admin user={user} onLogout={logout} />
      )}
      <KioskPinDialog
        open={pinOpen}
        onClose={() => setPinOpen(false)}
        onSuccess={() => {
          setPinOpen(false);
          setView("admin");
        }}
      />
    </div>
  );
}

export default App;
